import { Async, F1, SMap } from '@client/new/helpers/types';
import { useState, useRef, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  SectionName,
  DomainSection,
  SectionsSpecMap,
  SectionSchema,
  LandingPageModel,
  sectionsConstructorMap,
} from '../model/domain';
export type SectionFormRegistry = SMap<UseFormReturn<any>>;

export type LandingPageVM<TName extends SectionName = SectionName> = {
  sections: DomainSection<TName>[];
  formsMap: SectionFormRegistry;

  checkAll: () => Promise<{ valid: boolean }>;
  saveAll: () => Promise<{ success: boolean }>;
  reorderSections: F1<DomainSection<TName>[]>;
  getSection: (sid: string) => DomainSection<TName>[];
  getSectionData: <T extends SectionName>(t: T) => SectionsSpecMap[T]['data'];
  getSectionSchema: <T extends SectionName>(sid: string) => SectionSchema<T>;
  addForm: (id: string, form: UseFormReturn<any>) => void;
  removeForm: (id: string) => void;
  validateSection: (id: string) => Promise<boolean>;
  isDirty: (id: string) => boolean;
  hasErrors: (id: string) => boolean;
};

export const useLandingPageViewModel = <TName extends SectionName>(
  asyncModel: Async<LandingPageModel<TName>>,
): Async<LandingPageVM<TName>> => {
  const [formsMap, setFormsMap] = useState<SectionFormRegistry>({});
  const formUsageCountRef = useRef<Record<string, number>>({});
  const [sectionOrder, setSectionOrder] = useState<string[]>([]);

  useEffect(() => {
    if (asyncModel.type !== 'fetched') return;
    const modelIds = asyncModel.value.sections.map((s) => s.id);
    const orderIds = sectionOrder;
    const newIds = modelIds.filter((id) => !orderIds.includes(id));
    const nextOrder =
      sectionOrder.length === 0 ? modelIds : [...orderIds, ...newIds];
    setSectionOrder(nextOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncModel.type, JSON.stringify(sectionOrder)]);

  if (asyncModel.type !== 'fetched') return asyncModel;

  const model = asyncModel.value;

  const getOrderedSections = (): DomainSection<TName>[] => {
    if (sectionOrder.length === 0) return model.sections as any;

    const sectionsMap = new Map(
      model.sections.map((s) => [s.id, s as any as DomainSection<TName>]),
    );

    return sectionOrder
      .map((id) => sectionsMap.get(id))
      .filter((s): s is DomainSection<TName> => s !== undefined);
  };

  const orderedSections = getOrderedSections();

  return {
    type: 'fetched',
    value: {
      sections: orderedSections,
      formsMap,

      getSection: (id) => orderedSections.find((s) => s.id === id) as any,

      getSectionSchema: (id) => {
        const section = orderedSections.find((s) => s.id === id);
        return sectionsConstructorMap[section.type].schema;
      },

      getSectionData: (t) => asyncModel.value.sectionData[t as any],

      addForm: (id, form) =>
        setFormsMap((prev) => {
          formUsageCountRef.current[id] =
            (formUsageCountRef.current[id] ?? 0) + 1;
          if (prev[id]) return prev;
          return { ...prev, [id]: form };
        }),

      removeForm: (id) =>
        setFormsMap((prev) => {
          const nextCount = (formUsageCountRef.current[id] ?? 0) - 1;
          if (nextCount <= 0) {
            delete formUsageCountRef.current[id];
            if (!prev[id]) return prev;
            const { [id]: _, ...rest } = prev;
            return rest;
          }
          formUsageCountRef.current[id] = nextCount;
          return prev;
        }),

      validateSection: async (id) => {
        const form = formsMap[id];
        return form ? await form.trigger() : false;
      },

      checkAll: async () => {
        const forms = Object.values(formsMap);
        if (forms.length === 0) return { valid: true };
        const results = await Promise.all(forms.map((f) => f.trigger()));
        return { valid: results.every((r) => r) };
      },

      saveAll: async () => {
        const forms = Object.values(formsMap);
        if (forms.length === 0) return { success: true };

        const results = await Promise.all(forms.map((f) => f.trigger()));
        if (!results.every((r) => r)) return { success: false };

        const allData: SMap<DomainSection<TName>> = {};
        Object.entries(formsMap).forEach(
          ([id, form]) => (allData[id] = form.getValues()),
        );

        await asyncModel.value.save(Object.values(allData));
        return { success: true };
      },

      reorderSections: (newOrder) => setSectionOrder(newOrder.map((s) => s.id)),

      isDirty: (id) => formsMap[id]?.formState.isDirty ?? false,

      hasErrors: (id) => {
        const form = formsMap[id];
        return form ? Object.keys(form.formState.errors).length > 0 : false;
      },
    },
  };
};
